import React from 'react';
import { Button, Dialog, DialogContent, DialogFooter, DialogTrigger, TextField } from '@chatsdet/ui';
import { Play } from '@phosphor-icons/react';
import { useNavigate } from '@tanstack/react-router';
import { useStartPerfRun } from '../../../features/perf/usePerf';

/** Starts a run of a saved test; the API picks the base and resolves the typed head version. */
export function RunNowDialog({ testId }: { testId: string }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [head, setHead] = React.useState('');
  const start = useStartPerfRun(testId);
  const navigate = useNavigate();
  const [error, setError] = React.useState<string | null>(null);
  const version = head.trim();

  const submit = () => {
    setError(null);
    start.mutate(version, {
      onSuccess: ({ id }) => {
        setIsOpen(false);
        navigate({ to: '/performance/runs/$runId', params: { runId: id } });
      },
      onError: (e) => setError(e.message),
    });
  };

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen} size="md">
      <Button variant="primary" iconLeft={<Play size={16} weight="fill" />}>
        Run now
      </Button>
      <Dialog
        title="Run performance test"
        description="Measure a version against the one currently deployed."
        footer={
          <DialogFooter>
            <Button variant="secondary" onPress={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onPress={submit} isDisabled={version === '' || start.isPending}>
              {start.isPending ? 'Starting...' : 'Start run'}
            </Button>
          </DialogFooter>
        }
      >
        <DialogContent>
          <TextField
            label="Head version"
            description="A commit SHA, a push id or a tag (for example nightly)"
            value={head}
            onChange={(value) => {
              setHead(value);
              setError(null);
            }}
            errorMessage={error ?? undefined}
            autoFocus
          />
        </DialogContent>
      </Dialog>
    </DialogTrigger>
  );
}
